import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { LogOutIcon, UserIcon } from 'lucide-react'
import { clearAuthToken, isLoggedIn } from '../api/auth'

interface BottomNavProps {
  currentPage: 'discover' | 'profile' | 'showcase'
  onNavigate?: (page: 'discover' | 'profile') => void
}

const tabVariants = {
  idle: { scale: 1 },
  tap: { scale: 0.88 },
}

function discoverTabClasses(active: boolean) {
  return {
    shell: `w-10 h-10 flex items-center justify-center rounded-full transition-colors ${active ? 'bg-white/10' : 'group-hover:bg-white/5'}`,
    icon: `transition-colors ${active ? 'text-white' : 'text-white/50 group-hover:text-white'}`,
    label: `font-mono text-[9px] uppercase tracking-widest transition-colors ${active ? 'text-white' : 'text-white/50 group-hover:text-white/80'}`,
  }
}

function profileTabClasses(active: boolean) {
  return {
    shell: `w-10 h-10 flex items-center justify-center rounded-full transition-colors ${active ? 'bg-white/10' : 'group-hover:bg-white/5'}`,
    icon: `w-5 h-5 transition-colors ${active ? 'text-white' : 'text-white/50 group-hover:text-white'}`,
    label: `font-mono text-[9px] uppercase tracking-widest transition-colors ${active ? 'text-white' : 'text-white/50 group-hover:text-white/80'}`,
  }
}

function logoutTabClasses(isShowcase: boolean) {
  if (isShowcase) {
    return {
      shell:
        'w-10 h-10 flex items-center justify-center rounded-full transition-colors bg-black/[0.04] group-hover:bg-black/[0.08] border border-black/10',
      icon: 'w-5 h-5 text-black/55 group-hover:text-black transition-colors',
      label:
        'font-mono text-[9px] uppercase tracking-widest text-black/45 group-hover:text-black transition-colors',
    }
  }
  return {
    shell:
      'w-10 h-10 flex items-center justify-center rounded-full transition-colors group-hover:bg-white/5',
    icon: 'w-5 h-5 text-white/50 group-hover:text-white transition-colors',
    label:
      'font-mono text-[9px] uppercase tracking-widest text-white/50 group-hover:text-white/80 transition-colors',
  }
}

export function BottomNav({ currentPage, onNavigate }: BottomNavProps) {
  const navigate = useNavigate()
  const loggedIn = isLoggedIn()
  const discoverActive = currentPage === 'discover'
  const profileActive = currentPage === 'profile'
  const isShowcase = currentPage === 'showcase'
  const dc = discoverTabClasses(discoverActive)
  const pc = profileTabClasses(profileActive)
  const lc = logoutTabClasses(isShowcase)

  const handleLogout = () => {
    clearAuthToken()
    navigate('/login')
  }

  const waveIcon = (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={dc.icon}
    >
      <path d="M4 12c2.5 0 2.5-4 5-4s2.5 4 5 4 2.5-4 5-4" />
    </svg>
  )

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 backdrop-blur-xl border-t pb-safe ${
        currentPage === 'showcase'
          ? 'bg-white/90 border-black/10'
          : 'bg-white/5 border-white/10'
      }`}
    >
      <div
        className={`max-w-md mx-auto px-6 py-3 flex items-center ${
          loggedIn ? 'justify-between' : 'justify-around'
        }`}
      >
        {currentPage === 'showcase' ? (
          <>
            <motion.div whileTap={tabVariants.tap}>
              <Link
                to="/"
                state={{ slide: -1 }}
                className="flex flex-col items-center justify-center space-y-1 group"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full transition-colors bg-black/[0.04] group-hover:bg-black/[0.08] border border-black/10">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-black/55 group-hover:text-black transition-colors"
                  >
                    <path d="M4 12c2.5 0 2.5-4 5-4s2.5 4 5 4 2.5-4 5-4" />
                  </svg>
                </div>
                <span className="font-mono text-[9px] uppercase tracking-widest text-black/45 group-hover:text-black transition-colors">
                  DISCOVER
                </span>
              </Link>
            </motion.div>
            <motion.div whileTap={tabVariants.tap}>
              <Link
                to={{ pathname: '/', search: '?view=profile' }}
                state={{ slide: -1 }}
                className="flex flex-col items-center justify-center space-y-1 group"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full transition-colors bg-black/[0.04] group-hover:bg-black/[0.08] border border-black/10">
                  <UserIcon className="w-5 h-5 text-black/55 group-hover:text-black transition-colors" />
                </div>
                <span className="font-mono text-[9px] uppercase tracking-widest text-black/45 group-hover:text-black transition-colors">
                  PROFILE
                </span>
              </Link>
            </motion.div>
            {loggedIn && (
              <motion.button
                type="button"
                onClick={handleLogout}
                className="flex flex-col items-center justify-center space-y-1 group"
                whileTap={tabVariants.tap}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                aria-label="Log out"
              >
                <div className={lc.shell}>
                  <LogOutIcon className={lc.icon} />
                </div>
                <span className={lc.label}>LOGOUT</span>
              </motion.button>
            )}
          </>
        ) : (
          <>
            <motion.button
              className="flex flex-col items-center justify-center space-y-1 group"
              onClick={() => onNavigate?.('discover')}
              whileTap={tabVariants.tap}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            >
              <div className={dc.shell}>{waveIcon}</div>
              <span className={dc.label}>DISCOVER</span>
            </motion.button>

            <motion.button
              className="flex flex-col items-center justify-center space-y-1 group"
              onClick={() => onNavigate?.('profile')}
              whileTap={tabVariants.tap}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
            >
              <motion.div
                className={pc.shell}
                animate={
                  currentPage === 'profile' ? { scale: [1, 1.12, 1] } : { scale: 1 }
                }
                transition={{ duration: 0.3, ease: 'easeOut' }}
              >
                <UserIcon className={pc.icon} />
              </motion.div>
              <span className={pc.label}>PROFILE</span>
            </motion.button>

            {loggedIn && (
              <motion.button
                type="button"
                onClick={handleLogout}
                className="flex flex-col items-center justify-center space-y-1 group"
                whileTap={tabVariants.tap}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                aria-label="Log out"
              >
                <div className={lc.shell}>
                  <LogOutIcon className={lc.icon} />
                </div>
                <span className={lc.label}>LOGOUT</span>
              </motion.button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
